import { createContext, useMemo, useState, useCallback } from 'react';
import useAuth from '../hooks/useAuth';
import { getLeadsAdvanced } from '../API/leadManagementApi';
import { getSiteLeads, getAllMyLeads } from '../API/leadApi';
import { leadsParser } from '../utils/leadsParser';
import { LEAD_STATUSES } from '../constants/leadConstants';

const LeadsContext = createContext(null);

const LeadsContextProvider = ({ children }) => {
  const { userMe } = useAuth();

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [serverStats, setServerStats] = useState(null);
  const [expandedId, setExpandedId] = useState([]);
  const [isOpenLead, setIsOpenLead] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [fetchedFilters, setFetchedFilters] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    site: null,
    assignee: 'all',
    dateRange: 'all',
    keyword: '',
    domain: null,
    priority: 'all',
    orderValueRange: 'all',
    customDateFrom: null,
    customDateTo: null,
    searchLeadContent: false,
    companyName: null,
    salespersonId: 'all',
    isAvailable: 'all',
  });

  const loadLeads = useCallback(async ({ checkCache = false } = {}) => {
    // Check cache if requested - fetchedFilters being set means we've already fetched for these filters
    // Note: We check fetchedFilters existence, not leads.length, because empty results are valid cached results
    if (checkCache && fetchedFilters && JSON.stringify(filters) === JSON.stringify(fetchedFilters)) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Prepare advanced filtering parameters
      const advancedParams = {
        page: 0,
        limit: 1000,
        ...(filters.status !== 'all' && { status: filters.status }),
        ...(filters.keyword && { keyword: filters.keyword }),
        ...(filters.assignee !== 'all' && { assigneeId: filters.assignee }),
        ...(filters.priority !== 'all' && { priority: filters.priority }),
        ...(filters.domain && { domain: filters.domain }),
        ...(filters.searchLeadContent && { searchLeadContent: true }),
        ...(filters.companyName && { companyName: filters.companyName }),
        ...(filters.salespersonId && filters.salespersonId !== 'all' && { salespersonId: filters.salespersonId }),
        ...(filters.isAvailable && filters.isAvailable !== 'all' && { isAvailable: filters.isAvailable }),
        sortBy: 'lastActivity',
        sortOrder: 'desc',
      };

      let response;

      // Try to use advanced API if available, fallback to basic API
      try {
        response = await getLeadsAdvanced(advancedParams);
      } catch (advancedApiError) {
        console.warn('Advanced API not available, falling back to basic API:', advancedApiError);
        // Fallback to basic API
        if (userMe === 'salesperson') {
          response = await getAllMyLeads({ page: 0, limit: 1000 });
          response = response.data;
        } else {
          response = await getSiteLeads({ page: 0, limit: 1000 });
        }
      }

      // Handle different response formats - API returns leads as object with numeric keys sometimes
      let leadsData = response.leads || response.data?.leads || [];

      // Convert object with numeric keys to array if necessary
      if (leadsData && typeof leadsData === 'object' && !Array.isArray(leadsData)) {
        leadsData = Object.values(leadsData);
      }

      // Parse leads data if it's an array, otherwise return empty array
      const parsedLeads = Array.isArray(leadsData) ? leadsParser(leadsData) : [];

      // Enhance leads with proper field mapping from API
      const enhancedLeads = parsedLeads.map((lead) => {
        // Parse the data field if it contains JSON string
        let parsedData = {};
        if (typeof lead.data === 'string') {
          try {
            parsedData = JSON.parse(lead.data);
          } catch (e) {
            console.warn('Failed to parse lead data JSON:', e);
          }
        }

        // Calculate unread message count from API field or conversations array
        let unreadMessageCount = 0;
        if (lead.unread_message_count !== undefined) {
          // Use API-provided unread count
          unreadMessageCount = parseInt(lead.unread_message_count, 10) || 0;
        } else if (lead.unreadMessageCount !== undefined) {
          // Use camelCase version if provided
          unreadMessageCount = parseInt(lead.unreadMessageCount, 10) || 0;
        } else if (Array.isArray(lead.conversations) && lead.conversations.length > 0) {
          // Calculate from conversations array - count unread messages from customers
          unreadMessageCount = lead.conversations.filter(
            (conv) => !conv.isRead && !conv.is_read && conv.sender_type === 'customer',
          ).length;
        }

        return {
          ...lead,
          // Use actual status from API, fallback to NEW if missing
          status: lead.status || LEAD_STATUSES.NEW,
          // Map order_value from API to orderValue, ensure it's a number
          orderValue: lead.order_value ? parseFloat(lead.order_value) : 0,
          // Use assignee from API if present
          assignee: lead.assignee || (lead.assigned_to ? { id: lead.assigned_to } : null),
          // Use last_activity_at from API
          lastActivity: lead.last_activity_at || lead.lastActivity || lead.createdAt,
          // Use conversations from API or empty array
          conversations: lead.conversations || [],
          // Use actual priority from API
          priority: lead.priority || 'medium',
          // Unread message count for highlighting in list
          unreadMessageCount,
          hasUnreadMessages: unreadMessageCount > 0,
          // Map additional fields from API
          sendersEmail: lead.senders_email || lead.sendersEmail,
          domain: lead.site?.domain || lead.domain,
          content: lead.content || (parsedData ? Object.entries(parsedData).map(([key, value]) => `${key}: ${value}`).join('\n') : ''),
          // Add site information
          site: lead.site || {},
          // Add company information
          company: lead.company || {},
          // Add updater information
          updater: lead.updater || {},
          // Map additional API fields
          currency: lead.currency || 'SEK',
          estimatedCloseDate: lead.estimated_close_date || null,
          notes: lead.notes || '',
          tags: lead.tags || null,
          customFields: lead.custom_fields || null,
          activityCount: lead.activity_count || 0,
        };
      });

      setLeads(enhancedLeads);
      // Deep copy filters to ensure stability for comparison
      setFetchedFilters(JSON.parse(JSON.stringify(filters)));
      setLastFetched(new Date());

      // Use statistics from API response if available (advanced API)
      if (response.statistics) {
        const apiStats = {
          total: response.statistics.total || 0,
          new: response.statistics.statusDistribution?.new || 0,
          in_progress: response.statistics.statusDistribution?.in_progress || 0,
          deal: response.statistics.statusDistribution?.deal || 0,
          totalValue: response.statistics.totalOrderValue || 0,
        };
        setServerStats(apiStats);
      } else {
        setServerStats(null);
      }
    } catch (loadError) {
      console.error('Error loading leads:', loadError);
      setError(loadError);
    } finally {
      setLoading(false);
    }
  }, [filters, fetchedFilters, userMe]);

  const contextValue = useMemo(
    () => ({
      leads,
      setLeads,
      expandedId,
      setExpandedId,
      isOpenLead,
      setIsOpenLead,
      lastFetched,
      setLastFetched,
      fetchedFilters,
      setFetchedFilters,
      filters,
      setFilters,
      loading,
      setLoading,
      error,
      serverStats,
      loadLeads,
    }),
    [
      leads,
      expandedId,
      isOpenLead,
      lastFetched,
      filters,
      fetchedFilters,
      loading,
      error,
      serverStats,
      loadLeads,
    ],
  );

  return <LeadsContext.Provider value={contextValue}>{children}</LeadsContext.Provider>;
};

export { LeadsContext, LeadsContextProvider };
